import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { fetchProviders } from '../../auth/providers';
import type { ProviderInfo } from '../../auth/types';
import { EmporiumNav } from '../../components/EmporiumNav';
import { Colophon } from '../../components/Colophon';
import { SectionHeading, Eyebrow, Skeleton, ErrorTile } from '../../primitives';
import { LINKS } from '../../brand';
import { withBase } from '../../paths';
import { versionedUrl } from '../../utils/versionedUrl';

/**
 * MCP authorization consent page (SEC-0006 / prompt §8). The framework's embedded OAuth 2.1
 * Authorization Server drives the browser here mid-flow: it reads `rid` (authorization-code flow)
 * or `user_code` (device flow) from the query, fetches the consent context from the framework seam
 * (GET /oauth/request/{rid}), and renders the requesting client + the exact scopes + the resource
 * for the user to Allow or Deny. If the user is not signed in yet, it shows the same provider pills
 * the sign-in page renders, returning here after login. The OAuth PROTOCOL is framework-owned; this
 * page only renders the request and posts the user's decision.
 *
 * <para>Path is configured to the framework via Koan:Mcp:Auth:ConsentPath (reserved for when the
 * AS ships). Deliberately NOT under the account nav and NOT an AccountShell page: consent must
 * render for anonymous users (to offer sign-in), whereas AccountShell bounces anon to the
 * challenge.</para>
 *
 * <para><b>Sequencing.</b> The /oauth/* endpoints are SEC-0006 (Proposed, not yet shipped). This
 * page is built to the documented contract: render-testable against a mocked GET /oauth/request
 * response, but the live approve/deny round-trip + anti-forgery exchange only work once the
 * framework ships the Authorization Server.</para>
 */

interface ConsentScope {
    id: string;
    description?: string | null;
}

interface ConsentContext {
    /** The requesting client. Per SEC-0006 D5 the name is untrusted display-only (rendered escaped + "unverified"). */
    client: { name: string };
    /** The exact scopes requested (shown verbatim so a substitution between authorize and consent is visible). */
    scopes: ConsentScope[];
    /** The canonical resource id the token will be audience-bound to (SEC-0006 D2). */
    resource?: string | null;
    /** Whether a session principal is present; when false the page offers sign-in first. */
    user: { loggedIn: boolean };
    /** Provider list (ProviderDescriptor[], same shape as /.well-known/auth/providers). */
    providers?: ProviderInfo[];
    /** Formatted user_code to confirm on the device flow; null for the authorization-code flow. */
    user_code?: string | null;
}

type State =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; ctx: ConsentContext }
    | { status: 'submitting' };

export function AccountConnectPage() {
    const location = useLocation();
    const [state, setState] = useState<State>({ status: 'loading' });

    const { rid, userCode } = useMemo(() => {
        const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
        return { rid: params.get('rid'), userCode: params.get('user_code') };
    }, []);

    // The return URL for the sign-in pills: come straight back to this consent request after login.
    const returnUrl = useMemo(() => {
        if (typeof window === 'undefined') return withBase('/me/connect');
        return window.location.pathname + window.location.search;
    }, []);

    useEffect(() => {
        if (!rid && !userCode) {
            setState({ status: 'error', message: 'This page expects an authorization request (rid or user_code) in the URL.' });
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                // GET /oauth/request/{rid} (framework seam). For the device flow the rid is the user_code.
                const handle = rid ?? userCode!;
                const res = await fetch(`/oauth/request/${encodeURIComponent(handle)}`, {
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                });
                if (!res.ok) throw new Error(`Authorization request HTTP ${res.status}`);
                const ctx = (await res.json()) as ConsentContext;

                // Not logged in: hydrate the provider pills the same way the sign-in page does.
                if (!ctx.user?.loggedIn && (!ctx.providers || ctx.providers.length === 0)) {
                    try {
                        ctx.providers = await fetchProviders();
                    } catch {
                        // Leave providers empty; the not-logged-in branch shows an unavailable state.
                    }
                }
                if (!cancelled) setState({ status: 'ready', ctx });
            } catch (e) {
                if (!cancelled) setState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [rid, userCode]);

    const decide = async (decision: 'approve' | 'deny') => {
        const handle = rid ?? userCode!;
        setState({ status: 'submitting' });
        try {
            // The AS binds the request to this browser via a per-rid cookie (SEC-0006 D7); sending it
            // back with credentials:include IS the anti-forgery mechanism - no body/header token needed.
            const res = await fetch(`/oauth/request/${encodeURIComponent(handle)}/${decision}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            // A non-redirect error (e.g. expired rid, anti-forgery double-submit) must NOT fall
            // through to the success terminal - surface it as an error instead.
            if (!res.ok && !res.redirected) {
                setState({ status: 'error', message: `Authorization request failed (${res.status}).` });
                return;
            }
            // The framework owns the redirect from here (auth-code -> client redirect_uri; device ->
            // the terminal page). fetch follows same-origin redirects transparently; if it lands us
            // back here as JSON, send the user to the terminal page.
            if (res.redirected && res.url) {
                window.location.assign(res.url);
                return;
            }
            location.route(withBase('/me/connect/done'));
        } catch (e) {
            setState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
        }
    };

    return (
        <div class="flex min-h-full flex-col">
            <EmporiumNav current={null} />
            <main class="flex-1">
                <div class="mx-auto max-w-[480px] px-[clamp(20px,4vw,40px)] py-[clamp(28px,4vw,64px)]">
                    <article class="rounded-[14px] border border-rule bg-surface-raised p-[clamp(28px,4vw,44px)] shadow-[0_24px_60px_-32px_rgba(0,0,0,0.6)]">
                        <div class="mb-6 flex flex-col items-center gap-2.5">
                            <img
                                src={versionedUrl('/loporrit-mark-192.png')}
                                alt=""
                                width="64"
                                height="64"
                                class="block h-16 w-16 object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.45)]"
                            />
                            <Eyebrow tone="faint">Authorize access</Eyebrow>
                        </div>

                        {state.status === 'loading' && (
                            <div class="flex flex-col gap-3">
                                <Skeleton.Line height="24px" />
                                <Skeleton.Line height="50px" class="rounded-[10px]" />
                                <Skeleton.Line height="50px" class="rounded-[10px]" />
                            </div>
                        )}

                        {state.status === 'error' && (
                            <ErrorTile title="Couldn't load this authorization request" details={state.message} />
                        )}

                        {state.status === 'submitting' && (
                            <p class="py-8 text-center text-sm text-fg-muted">Submitting your decision…</p>
                        )}

                        {state.status === 'ready' && <ConsentBody ctx={state.ctx} userCode={userCode} returnUrl={returnUrl} onDecide={decide} />}
                    </article>
                </div>
            </main>
            <Colophon emporiumHref="/emporium" studioHref="/studio" packHref={LINKS.github.releasesLatest} />
        </div>
    );
}

function ConsentBody({
    ctx,
    userCode,
    returnUrl,
    onDecide,
}: {
    ctx: ConsentContext;
    userCode: string | null;
    returnUrl: string;
    onDecide: (decision: 'approve' | 'deny') => void;
}) {
    const clientName = ctx.client?.name?.trim() || 'An application';
    // Prefer the formatted user_code from the response body; fall back to the query param.
    const deviceCode = ctx.user_code ?? userCode;

    // Not signed in: offer the same provider pills the sign-in page uses, returning here afterwards.
    if (!ctx.user?.loggedIn) {
        const providers = ctx.providers ?? [];
        return (
            <div>
                <SectionHeading
                    title="Sign in to continue"
                    subtitle={`"${clientName}" wants to connect to your Gposingway account. Sign in to review what it's asking for.`}
                    size="lg"
                    align="center"
                    as="h1"
                    class="mb-6"
                />
                {providers.length === 0 ? (
                    <p class="text-center text-sm text-fg-muted">No sign-in providers are available right now.</p>
                ) : (
                    <div class="flex flex-col gap-3" role="group" aria-label="Sign-in providers">
                        {providers.map((p) => {
                            const sep = p.challengeUrl.indexOf('?') === -1 ? '?' : '&';
                            const href = `${p.challengeUrl}${sep}return=${encodeURIComponent(returnUrl)}`;
                            return (
                                <a
                                    key={p.id}
                                    href={href}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.location.assign(href);
                                    }}
                                    class="inline-flex items-center gap-3.5 rounded-[10px] border border-rule-strong bg-surface-base px-4 py-3.5 text-[14.5px] font-medium text-fg-primary transition-colors hover:-translate-y-px"
                                >
                                    <span class="flex-1">Continue with {p.name || p.id}</span>
                                    <span class="font-mono text-[14px] text-fg-faint">→</span>
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // Signed in: show the client, the requested scopes, the resource, and Allow / Deny.
    return (
        <div>
            <SectionHeading
                title={`Allow "${clientName}"?`}
                subtitle="This application is requesting access to your Gposingway account. It has not been verified by Gposingway."
                size="lg"
                align="center"
                as="h1"
                class="mb-5"
            />

            {deviceCode && (
                <div class="mb-5 rounded-[10px] border border-accent-soft bg-accent-glow px-4 py-3 text-center">
                    <div class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">Confirm this code matches your device</div>
                    <div class="mt-1 font-mono text-2xl tracking-[0.3em] text-fg-primary">{deviceCode}</div>
                </div>
            )}

            <div class="mb-5 rounded-[10px] border border-rule bg-surface-base p-4">
                <div class="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">It will be able to</div>
                {ctx.scopes && ctx.scopes.length > 0 ? (
                    <ul class="flex flex-col gap-2">
                        {ctx.scopes.map((s) => (
                            <li key={s.id} class="flex gap-2 text-sm text-fg-primary">
                                <span aria-hidden="true" class="text-accent">•</span>
                                <span>
                                    {s.description?.trim() || s.id}
                                    <span class="ml-2 font-mono text-[11px] text-fg-faint">{s.id}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p class="text-sm text-fg-muted">No specific permissions requested.</p>
                )}
                {ctx.resource && (
                    <p class="mt-3 border-t border-rule pt-3 font-mono text-[11px] text-fg-faint">
                        Resource: {ctx.resource}
                    </p>
                )}
            </div>

            <div class="flex gap-3">
                <button
                    type="button"
                    onClick={() => onDecide('deny')}
                    class="flex-1 rounded-full border border-rule-strong px-4 py-2.5 text-[14px] font-medium text-fg-secondary transition-colors hover:border-fg-muted hover:text-fg-primary"
                >
                    Deny
                </button>
                <button
                    type="button"
                    onClick={() => onDecide('approve')}
                    class="flex-1 rounded-full border border-accent bg-accent px-4 py-2.5 text-[14px] font-medium text-surface-base transition-colors hover:bg-accent-soft"
                >
                    Allow
                </button>
            </div>

            <p class="mt-6 border-t border-rule pt-4 text-center text-[12px] leading-[1.55] text-fg-faint">
                You can revoke this access at any time from your account.
            </p>
        </div>
    );
}
